/**
 * CR305: Deal Card Render Signature Debug System
 * 
 * Provides tooling to expose and verify rendering pipeline divergence across
 * Layout Editor, Deal Cards UI, and Theme Labs screens.
 * 
 * URL-gated: Only active when ?debugCardSig=1 is present.
 */

// Import renderer rollout for auto-reason detection
import { getDealCardRendererModeSync, type RendererReason } from "@/lib/deals/rendererRollout";

// ================================================================
// TYPES
// ================================================================

export type DealCardScreen = 
  | "layout_editor" 
  | "deal_cards_ui" 
  | "theme_labs" 
  | "unknown";

export type DealSource = 
  | "production_deals" 
  | "cr075_production_deals" 
  | "v_layout_studio_sample_deals" 
  | "mock" 
  | "fixture"
  | "unknown";

export type ConfigResolver = 
  | "layout_studio_resolver" 
  | "deal_card_config_provider" 
  | "theme_labs_resolver" 
  | "unknown";

export type TokensSource = 
  | "layout_preset" 
  | "theme_labs_preview" 
  | "global_theme_tokens"
  | "unknown";

export interface DealCardRenderSignature {
  /** Which screen is rendering */
  screen: DealCardScreen;
  
  /** Deal being rendered */
  deal: {
    id: string | null;
    dedupe_key: string | null;
    merchant_key: string | null;
    deal_type: string | null;
    source: DealSource;
  };
  
  /** Layout configuration */
  layout: {
    environment: "staging" | "publish" | "draft" | "unknown";
    active_preset_id: string | null;
    preset_updated_at: string | null;
  };
  
  /** Theme tokens source */
  theme: {
    tokens_source: TokensSource;
    theme_id: string | null;
  };
  
  /** Config resolution path */
  config: {
    resolver: ConfigResolver;
    uses_deal_card_display_helpers: boolean;
  };
  
  /** Metadata */
  _meta: {
    generated_at: string;
    version: "1.0";
  };
}

export interface PartialSignatureInput {
  screen?: DealCardScreen;
  deal?: Partial<DealCardRenderSignature["deal"]>;
  layout?: Partial<DealCardRenderSignature["layout"]>;
  theme?: Partial<DealCardRenderSignature["theme"]>;
  config?: Partial<DealCardRenderSignature["config"]>;
}

// ================================================================
// URL FLAG CHECK (SAFE IFRAME DETECTION)
// ================================================================

export type DealCardSignatureRenderer = "v1" | "v2";

/**
 * Safe iframe detection that never reads parent/top.location
 * (which would throw cross-origin errors)
 */
export function isInIframe(): boolean {
  if (typeof window === "undefined") return false;
  try {
    // Safe check: does NOT touch parent/top.location
    return window.self !== window.top;
  } catch {
    // If accessing window.top throws (cross-origin), we are embedded.
    return true;
  }
}

function hasTruthyQueryParam(names: string[]): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return names.some((n) => {
    const v = params.get(n);
    return v === "1" || v === "true" || v === "yes";
  });
}

/**
 * Check if the debug card signature flag is enabled via URL param.
 * Works in both normal contexts and iframe contexts (e.g., Layout Studio previews).
 * @returns true if ?debugCardSig=1 is in the URL (or parent iframe URL)
 */
export function isDebugCardSigEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return hasTruthyQueryParam(["debugCardSig"]);
}

/**
 * Determine if the signature strip should render.
 * Shows in iframe by default (admin preview), or force via query param.
 */
export function shouldRenderDealCardSignatureStrip(): boolean {
  if (typeof window === "undefined") return false;

  // Hard kill switch
  if (hasTruthyQueryParam(["hideDealCardSignature", "hide_sig"])) return false;

  // Show in iframe by default (admin preview), or force via query param
  return hasTruthyQueryParam(["debugDealCardSignature", "dealCardSig", "sig", "debugCardSig"]) || isInIframe();
}

/**
 * Renderer reason type for signature display
 */
export type SignatureRendererReason = 
  | "forced_by_rollout"
  | "forced_by_query_param"
  | "manual"
  | "default";

/**
 * Build a compact signature string for the debug strip
 * 
 * @param renderer - "v1" or "v2"
 * @param deal - Deal object with id, dedupe_key, etc.
 * @param reason - Optional reason for renderer selection (auto-detected if not provided)
 */
export function buildDealCardSignature(
  renderer: DealCardSignatureRenderer, 
  deal: any,
  reason?: SignatureRendererReason
): string {
  // Auto-detect reason from rollout config if not provided
  const effectiveReason = reason ?? (() => {
    try {
      const decision = getDealCardRendererModeSync();
      return decision.reason as SignatureRendererReason;
    } catch {
      return undefined;
    }
  })();
  const id = (deal?.id ?? deal?.deal_id ?? "").toString();
  const dedupeKey = (deal?.dedupe_key ?? deal?.dedupeKey ?? "").toString();
  const merchant = (
    deal?.merchant_key ??
    deal?.merchantKey ??
    deal?.merchant_name ??
    deal?.merchantName ??
    deal?.merchant ??
    ""
  ).toString();
  const type = (deal?.deal_type ?? deal?.dealType ?? "").toString();
  const schema = (deal?.schema_version ?? deal?.schemaVersion ?? "").toString();

  const idShort = id ? id.slice(0, 8) : "no-id";
  const dkShort = dedupeKey ? dedupeKey.slice(0, 10) : "no-dk";
  
  // Add reason indicator
  const reasonLabel = effectiveReason === "forced_by_rollout" ? "🔒" 
    : effectiveReason === "forced_by_query_param" ? "🔗"
    : null;

  return [
    renderer.toUpperCase(),
    reasonLabel,
    type || "unknown",
    merchant || "unknown",
    idShort,
    dkShort,
    schema ? `s${schema}` : null,
  ]
    .filter(Boolean)
    .join(" • ");
}

// ================================================================
// SIGNATURE BUILDER
// ================================================================

/**
 * Build a complete DealCardRenderSignature from partial input.
 * Fills in defaults for any missing fields.
 */
export function buildDealCardRenderSignature(
  input: PartialSignatureInput
): DealCardRenderSignature {
  return {
    screen: input.screen ?? "unknown",
    deal: {
      id: input.deal?.id ?? null,
      dedupe_key: input.deal?.dedupe_key ?? null,
      merchant_key: input.deal?.merchant_key ?? null,
      deal_type: input.deal?.deal_type ?? null,
      source: input.deal?.source ?? "unknown",
    },
    layout: {
      environment: input.layout?.environment ?? "unknown",
      active_preset_id: input.layout?.active_preset_id ?? null,
      preset_updated_at: input.layout?.preset_updated_at ?? null,
    },
    theme: {
      tokens_source: input.theme?.tokens_source ?? "unknown",
      theme_id: input.theme?.theme_id ?? null,
    },
    config: {
      resolver: input.config?.resolver ?? "unknown",
      uses_deal_card_display_helpers: input.config?.uses_deal_card_display_helpers ?? false,
    },
    _meta: {
      generated_at: new Date().toISOString(),
      version: "1.0",
    },
  };
}

// ================================================================
// COMPARISON HELPERS
// ================================================================

/**
 * Compare two signatures and return a list of differences.
 * Useful for debugging divergence across screens.
 */
export function compareSignatures(
  a: DealCardRenderSignature,
  b: DealCardRenderSignature
): string[] {
  const diffs: string[] = [];
  
  // Screen
  if (a.screen !== b.screen) {
    diffs.push(`screen: ${a.screen} vs ${b.screen}`);
  }
  
  // Deal
  if (a.deal.id !== b.deal.id) diffs.push(`deal.id: ${a.deal.id} vs ${b.deal.id}`);
  if (a.deal.source !== b.deal.source) diffs.push(`deal.source: ${a.deal.source} vs ${b.deal.source}`);
  if (a.deal.deal_type !== b.deal.deal_type) diffs.push(`deal.deal_type: ${a.deal.deal_type} vs ${b.deal.deal_type}`);
  
  // Layout
  if (a.layout.active_preset_id !== b.layout.active_preset_id) {
    diffs.push(`layout.active_preset_id: ${a.layout.active_preset_id} vs ${b.layout.active_preset_id}`);
  }
  if (a.layout.environment !== b.layout.environment) {
    diffs.push(`layout.environment: ${a.layout.environment} vs ${b.layout.environment}`);
  }
  
  // Theme
  if (a.theme.tokens_source !== b.theme.tokens_source) {
    diffs.push(`theme.tokens_source: ${a.theme.tokens_source} vs ${b.theme.tokens_source}`);
  }
  
  // Config
  if (a.config.resolver !== b.config.resolver) {
    diffs.push(`config.resolver: ${a.config.resolver} vs ${b.config.resolver}`);
  }
  if (a.config.uses_deal_card_display_helpers !== b.config.uses_deal_card_display_helpers) {
    diffs.push(`config.uses_deal_card_display_helpers: ${a.config.uses_deal_card_display_helpers} vs ${b.config.uses_deal_card_display_helpers}`);
  }
  
  return diffs;
}
